// Map geometry constants live here (not in index.ts) so this module has no dependency on
// index.ts — index.ts re-exports them, avoiding a circular import (index.ts already depends
// on this module for parseTerrain/TERRAIN_ROWS at load time).
export const MAP_GRID_SIZE = 101; // physical IsometricMap grid (client-side tiles)
export const MAP_CENTER = 50; // town hall tile; buildable square is centered here
export const START_RADIUS = 8; // minimum map radius (17×17 for a fresh map)
export const MAX_RADIUS = 45; // cap — keeps buildable square inside the grid
const CHAR_TO_CELL = {
    ".": "grass",
    ",": "grass2",
    "~": "water",
    T: "tree",
};
export function parseTerrain(rows) {
    if (rows.length !== MAP_GRID_SIZE) {
        throw new Error(`Terrain must have ${MAP_GRID_SIZE} rows, got ${rows.length}`);
    }
    const grid = rows.map((row, y) => {
        if (row.length !== MAP_GRID_SIZE) {
            throw new Error(`Terrain row ${y} must have ${MAP_GRID_SIZE} chars, got ${row.length}`);
        }
        return [...row].map((ch, x) => {
            const cell = CHAR_TO_CELL[ch];
            if (!cell)
                throw new Error(`Unknown terrain char '${ch}' at (${x}, ${y})`);
            return cell;
        });
    });
    const inGrid = (x, y) => x >= 0 && x < MAP_GRID_SIZE && y >= 0 && y < MAP_GRID_SIZE;
    return {
        // Out-of-grid reads as tree — infinite forest surrounds the authored 101×101 area.
        cellAt(x, y) {
            return inGrid(x, y) ? grid[y][x] : "tree";
        },
        blocksBuilding(x, y) {
            if (!inGrid(x, y))
                return true; // out-of-grid is forest (see cellAt) — always blocked
            const cell = grid[y][x];
            return cell === "water" || cell === "tree";
        },
        isWater(x, y) {
            return inGrid(x, y) && grid[y][x] === "water";
        },
    };
}
// Flood-fills the 4-connected grass/grass2 region containing the town hall, restricted
// to the MAX_RADIUS square. Used by validateTerrain to check the "main continent" invariant.
function mainContinentSize(terrain) {
    const inBounds = (x, y) => Math.abs(x - MAP_CENTER) <= MAX_RADIUS &&
        Math.abs(y - MAP_CENTER) <= MAX_RADIUS;
    const passable = (x, y) => !terrain.blocksBuilding(x, y);
    const seen = new Set([`${MAP_CENTER},${MAP_CENTER}`]);
    const stack = [[MAP_CENTER, MAP_CENTER]];
    while (stack.length > 0) {
        const [x, y] = stack.pop();
        for (const [nx, ny] of [
            [x + 1, y],
            [x - 1, y],
            [x, y + 1],
            [x, y - 1],
        ]) {
            const key = `${nx},${ny}`;
            if (seen.has(key) || !inBounds(nx, ny) || !passable(nx, ny))
                continue;
            seen.add(key);
            stack.push([nx, ny]);
        }
    }
    return seen.size;
}
const MIN_CONTINENT_SIZE = 2500;
// Authoring invariants — throws on violation. Called at server boot and by the generator script.
export function validateTerrain(terrain) {
    for (let y = -START_RADIUS; y <= START_RADIUS; y++) {
        for (let x = -START_RADIUS; x <= START_RADIUS; x++) {
            if (Math.max(Math.abs(x), Math.abs(y)) > START_RADIUS)
                continue;
            const cell = terrain.cellAt(MAP_CENTER + x, MAP_CENTER + y);
            if (cell !== "grass" && cell !== "grass2") {
                throw new Error(`Terrain: tile within START_RADIUS of center must be grass, found '${cell}' at offset (${x}, ${y})`);
            }
        }
    }
    const continentSize = mainContinentSize(terrain);
    if (continentSize < MIN_CONTINENT_SIZE) {
        throw new Error(`Terrain: main continent has only ${continentSize} tiles, need >= ${MIN_CONTINENT_SIZE}`);
    }
}
// Computes a 4-bit edge mask for street/terrain autotiling.
// bit 0 = north (y-1), bit 1 = east (x+1), bit 2 = south (y+1), bit 3 = west (x-1).
export function edgeBitmask(x, y, isSet) {
    let mask = 0;
    if (isSet(x, y - 1))
        mask |= 1;
    if (isSet(x + 1, y))
        mask |= 2;
    if (isSet(x, y + 1))
        mask |= 4;
    if (isSet(x - 1, y))
        mask |= 8;
    return mask;
}
